import { legalAcceptanceRequired, presentLegalAcceptance, throwIfLegalAcceptanceRequired } from "./legal_acceptance.js"
import { PDFDataRangeTransport, getDocument } from "pdfjs-dist/legacy/build/pdf.mjs"
import { OfflinePdfStore, offlineCopyExpired } from "./offline_pdf_store.js"

const RANGE_CHUNK_SIZE = 1_048_576
const MAX_RANGE_REQUEST_SIZE = 8_388_608

class PdfRangeTransport extends PDFDataRangeTransport {
  constructor({ source, length, signal, onError }) {
    super(length, null, true)
    this.source = source
    this.signal = signal
    this.onError = onError
    this.requests = new Set()
  }

  requestDataRange(begin, end) {
    const controller = new AbortController()
    const abort = () => controller.abort()
    this.requests.add(controller)
    this.signal?.addEventListener("abort", abort, { once: true })
    if (this.signal?.aborted) controller.abort()

    this.source.read(begin, end, controller.signal)
      .then((data) => {
        if (data.byteLength !== end - begin) throw new Error("PDF range source returned an unexpected length")
        this.onDataRange(begin, data)
      })
      .catch((error) => {
        if (error.name === "AbortError") return

        console.error("Unable to load PDF byte range", error)
        this.abort()
        this.onError(error)
      })
      .finally(() => {
        this.requests.delete(controller)
        this.signal?.removeEventListener("abort", abort)
      })
  }

  abort() {
    this.requests.forEach((controller) => controller.abort())
    this.requests.clear()
  }
}

class NetworkPdfRangeSource {
  constructor({ url, length, etag }) {
    this.url = url
    this.length = length
    this.etag = etag
  }

  async read(begin, end, signal) {
    const data = new Uint8Array(end - begin)

    for (let offset = begin; offset < end; offset += MAX_RANGE_REQUEST_SIZE) {
      const rangeEnd = Math.min(offset + MAX_RANGE_REQUEST_SIZE, end)
      data.set(await this.fetchRange(offset, rangeEnd, signal), offset - begin)
    }
    return data
  }

  async fetchRange(begin, end, signal) {
    const headers = { Range: `bytes=${begin}-${end - 1}` }
    if (this.etag) headers["If-Range"] = this.etag
    const response = await fetch(this.url, {
      method: "GET",
      credentials: "same-origin",
      headers,
      signal
    })

    const legal = await legalAcceptanceRequired(response)
    if (legal) presentLegalAcceptance(legal)
    await throwIfLegalAcceptanceRequired(response)
    if (response.status !== 206) throw new Error(`PDF range request returned ${response.status}`)
    if (response.headers.get("Content-Range") !== `bytes ${begin}-${end - 1}/${this.length}`) {
      throw new Error("PDF range response does not match the requested bytes")
    }
    if (this.etag && response.headers.get("ETag") !== this.etag) throw new Error("PDF range response ETag changed")

    const data = new Uint8Array(await response.arrayBuffer())
    if (data.byteLength !== end - begin) throw new Error("PDF range response has an unexpected length")
    return data
  }
}

class OpfsPdfRangeSource {
  constructor({ record, store }) {
    this.record = record
    this.store = store
  }

  read(begin, end) {
    if (this.record.legalAcceptanceRequired) throw new Error("Offline PDF is unavailable")
    return this.store.readRange(this.record, begin, end)
  }
}

export async function loadPdfDocument({ url, signal, options = {}, offlineRecord = null, store = new OfflinePdfStore() }) {
  let source
  let length

  if (offlineRecord) {
    if (offlineRecord.legalAcceptanceRequired || !offlineRecord.ready || offlineCopyExpired(offlineRecord)) throw new Error("Offline PDF is unavailable")
    if (!await store.verifyComplete(offlineRecord)) throw new Error("Offline PDF chunks are incomplete")
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError")
    if (offlineRecord.legalAcceptanceRequired) throw new Error("Offline PDF is unavailable")
    source = new OpfsPdfRangeSource({ record: offlineRecord, store })
    length = offlineRecord.byteSize
  } else {
    const metadata = await networkMetadata(url, signal)
    source = new NetworkPdfRangeSource({ url, ...metadata })
    length = metadata.length
  }

  let loadingTask
  let rangeFailureReported = false
  let rejectRangeFailure
  const rangeFailure = new Promise((_, reject) => {
    rejectRangeFailure = reject
  })
  const range = new PdfRangeTransport({
    source,
    length,
    signal,
    onError: (error) => {
      if (rangeFailureReported) return

      rangeFailureReported = true
      rejectRangeFailure(error)
      loadingTask?.destroy().catch(() => {})
    }
  })

  loadingTask = getDocument({
    ...options,
    range,
    rangeChunkSize: RANGE_CHUNK_SIZE,
    disableStream: true,
    disableAutoFetch: true
  })
  Object.defineProperty(loadingTask, "promise", {
    value: Promise.race([loadingTask.promise, rangeFailure])
  })
  return loadingTask
}

async function networkMetadata(url, signal) {
  const response = await fetch(url, {
    method: "HEAD",
    credentials: "same-origin",
    headers: { Accept: "application/pdf" },
    signal
  })

  const legal = await legalAcceptanceRequired(response)
  if (legal) presentLegalAcceptance(legal)
  await throwIfLegalAcceptanceRequired(response)
  if (!response.ok) throw new Error(`PDF metadata request returned ${response.status}`)
  if (response.headers.get("Accept-Ranges") !== "bytes") throw new Error("PDF byte ranges are unavailable")
  if (response.headers.get("Content-Type")?.split(";", 1)[0] !== "application/pdf") {
    throw new Error("PDF metadata returned an unexpected content type")
  }

  const length = Number(response.headers.get("Content-Length"))
  if (!Number.isSafeInteger(length) || length <= 0) throw new Error("PDF metadata has an invalid length")
  return { length, etag: response.headers.get("ETag") }
}
